import { useEffect, useState } from 'react';
import { Plus, Pencil, Power, PowerOff, Building2 } from 'lucide-react';
import { clientesApi } from '../api/endpoints';
import { Modal } from '../components/ui/Modal';
import { Spinner } from '../components/ui/Spinner';
import { SearchInput } from '../components/ui/SearchInput';
import { EmptyState } from '../components/ui/EmptyState';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Alert } from '../components/ui/Alert';
import { useAuthStore } from '../auth/authStore';

const empty = { nombreComercial: '', razonSocial: '', rfc: '', direccion: '', telefono: '', email: '', logoPath: '', ciudad: '' };

export function Clientes() {
  const usuario = useAuthStore((s) => s.usuario);
  const isAdmin = usuario?.rol === 'Admin';
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setError(null);
    try { setData(await clientesApi.list()); }
    catch (e) { setError(e?.response?.data?.error || 'Error al cargar clientes'); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await clientesApi.update(editing.id, { ...form, activo: editing.activo });
      else await clientesApi.create(form);
      setModalOpen(false);
      await load();
    } catch (e) { setError(e?.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const toggle = async (c) => {
    setConfirm({ title: c.activo ? '¿Desactivar cliente?' : '¿Activar cliente?', message: c.nombreComercial, onConfirm: async () => {
      await clientesApi.update(c.id, { ...c, activo: !c.activo });
      await load();
      setConfirm(null);
    }});
  };

  const filtered = (data ?? []).filter(c => !search || c.nombreComercial.toLowerCase().includes(search.toLowerCase()) || (c.rfc || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar cliente por nombre o RFC..." className="max-w-md" />
        {isAdmin && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-lg shrink-0">
            <Plus size={16} /> <span className="hidden sm:inline">Nuevo Cliente</span>
          </button>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {data === null ? (
        <div className="flex justify-center py-12"><Spinner size={32} /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Building2} title="Sin clientes" description="No hay clientes registrados." action={isAdmin && <button onClick={openCreate} className="px-4 py-2 bg-cyan-500 text-white rounded-md">Crear primer cliente</button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((c) => (
            <div key={c.id} className="bg-panel border border-border rounded-xl p-4 hover:border-cyan-500/40 transition group">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-white truncate">{c.nombreComercial}</h3>
                  <p className="text-xs text-muted truncate">{c.razonSocial}</p>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0 ml-2 md:opacity-0 md:group-hover:opacity-100">
                    <button onClick={() => openEdit(c)} className="p-1.5 rounded text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10" title="Editar">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => toggle(c)} className={`p-1.5 rounded ${c.activo ? 'text-slate-400 hover:text-rose-400 hover:bg-rose-500/10' : 'text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10'}`} title={c.activo ? 'Desactivar' : 'Activar'}>
                      {c.activo ? <PowerOff size={14} /> : <Power size={14} />}
                    </button>
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-300 space-y-1 mb-3">
                <div className="flex gap-2"><span className="text-muted w-14 shrink-0">RFC</span><span className="font-mono truncate">{c.rfc || '—'}</span></div>
                <div className="flex gap-2"><span className="text-muted w-14 shrink-0">📍</span><span className="truncate">{c.ciudad || '—'}</span></div>
                <div className="flex gap-2"><span className="text-muted w-14 shrink-0">📞</span><span className="truncate">{c.telefono || '—'}</span></div>
                <div className="flex gap-2"><span className="text-muted w-14 shrink-0">📧</span><span className="truncate">{c.email || '—'}</span></div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-[10px] text-muted">{c.empleadosCount} empleados · {c.relacionesCount} relaciones</span>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ${c.activo ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
                  {c.activo ? 'ACTIVO' : 'INACTIVO'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Cliente' : 'Nuevo Cliente'} size="md">
        <div className="space-y-3">
          <Field label="Nombre comercial *" value={form.nombreComercial} onChange={(v) => setForm({ ...form, nombreComercial: v })} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Razón social" value={form.razonSocial} onChange={(v) => setForm({ ...form, razonSocial: v })} />
            <Field label="RFC" value={form.rfc} onChange={(v) => setForm({ ...form, rfc: v })} />
          </div>
          <Field label="Dirección" value={form.direccion} onChange={(v) => setForm({ ...form, direccion: v })} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Teléfono" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Ciudad" value={form.ciudad} onChange={(v) => setForm({ ...form, ciudad: v })} />
            <Field label="Ruta Logo (opcional)" value={form.logoPath} onChange={(v) => setForm({ ...form, logoPath: v })} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-border">
          <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded border border-border text-slate-300 hover:bg-panel-2 text-sm">Cancelar</button>
          <button onClick={save} disabled={saving || !form.nombreComercial} className="px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold disabled:opacity-50">
            {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </Modal>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message} onCancel={() => setConfirm(null)} onConfirm={confirm?.onConfirm} />
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white focus:outline-none focus:border-cyan-500"
      />
    </div>
  );
}
